import { z } from "zod";
import { ApiClientError, parseApiError } from "./errors.js";
import {
  type AdminCatalogs,
  AdminCatalogsResponseSchema,
  type AmoAccount,
  AmoAccountSchema,
  type AmoOAuthStart,
  AmoOAuthStartSchema,
  type AmoStatus,
  AmoStatusSchema,
  type AppUser,
  AppUserSchema,
  AppUsersResponseSchema,
  type AuthBootstrap,
  AuthBootstrapSchema,
  type AuthMethods,
  AuthMethodsSchema,
  type BackupDownloadResult,
  type BackupMetadata,
  BackupMetadataSchema,
  type CalendarQueryResult,
  CalendarQueryResultSchema,
  type CatalogFieldValue,
  type CreateDealEnrollmentResult,
  CreateDealEnrollmentResultSchema,
  type DealContext,
  DealContextSchema,
  type DealLedger,
  DealLedgerSchema,
  type DealTransferPlan,
  DealTransferPlanSchema,
  type EditableAdminCatalog,
  type Enrollment,
  EnrollmentSchema,
  EnrollmentsByLeadSchema,
  EnrollmentsListSchema,
  type FinanceSettings,
  FinanceSettingsSchema,
  type FxHistoryEntry,
  FxHistoryResponseSchema,
  type FxQuote,
  FxQuoteSchema,
  type HandoffNextResult,
  HandoffNextResultSchema,
  HandoffSessionSchema,
  type Instructor,
  type InstructorMapping,
  InstructorMappingsResponseSchema,
  InstructorNoteResultSchema,
  type InstructorScheduleView,
  InstructorScheduleViewSchema,
  type InstructorStudent,
  InstructorStudentsResponseSchema,
  InstructorsResponseSchema,
  type LedgerContract,
  LedgerContractSchema,
  type LedgerMethod,
  type LedgerPayment,
  LedgerPaymentSchema,
  LedgerPaymentsResponseSchema,
  LedgerReceiptSchema,
  LessonsListSchema,
  type Location,
  LocationsResponseSchema,
  type LogoutResult,
  LogoutResultSchema,
  type NextUnscheduledLesson,
  NextUnscheduledResponseSchema,
  OccupancyResponseSchema,
  OidcStartSchema,
  type OperationalLogItem,
  OperationalLogsResponseSchema,
  type OriginalCurrency,
  type OwnLessonDetail,
  OwnLessonDetailSchema,
  type PasswordLoginResult,
  PasswordLoginResultSchema,
  type PaymentMethod,
  type PaymentReport,
  PaymentReportSchema,
  PaymentReportsResponseSchema,
  type ScheduledLessonRecord,
  ScheduledLessonRecordSchema,
  type ScheduleMutationResult,
  ScheduleMutationResultSchema,
  type Session,
  SessionSchema,
  type SettlementMethod,
  type SyncCatalogsResponse,
  SyncCatalogsResponseSchema,
  type Takings,
  TakingsResponseSchema,
  type UnscheduledLesson,
  UnscheduledLessonsResponseSchema,
  type UpdateCatalogElementResponse,
  UpdateCatalogElementResponseSchema,
  type Venue,
  VenueMappingResultSchema,
  VenueSchema,
  VenuesResponseSchema,
} from "./schemas.js";

export interface ApiClientOptions {
  readonly baseUrl?: string;
  readonly getCsrfToken?: () => string | null | undefined;
  readonly fetchImpl?: typeof fetch;
}

export interface CalendarRangeQuery {
  readonly from: string;
  readonly to: string;
  readonly instructorId?: string;
}

export interface ScheduleLessonInput {
  readonly startsAt: string;
  readonly venueId: string;
  readonly instructorId: string;
  readonly idempotencyKey: string;
  readonly capacityOverrideReason?: string;
  readonly postLessonBufferMinutes?: number;
  readonly durationMinutes?: number;
}

export interface RescheduleLessonInput {
  readonly expectedVersion: number;
  readonly startsAt: string;
  readonly reason: string;
  readonly idempotencyKey: string;
  readonly venueId?: string;
  readonly instructorId?: string;
  readonly capacityOverrideReason?: string;
  readonly postLessonBufferMinutes?: number;
  readonly durationMinutes?: number;
}

export interface VersionedReasonInput {
  readonly expectedVersion: number;
  readonly reason: string;
  readonly idempotencyKey: string;
}

export interface VersionedActionInput {
  readonly expectedVersion: number;
  readonly idempotencyKey: string;
  readonly comment?: string;
}

export interface SubmitPaymentInput {
  readonly enrollmentId: string;
  readonly amount: number;
  readonly currency: string;
  readonly paymentMethod: PaymentMethod;
  readonly idempotencyKey: string;
  readonly notes?: string;
  readonly paymentDate?: string;
  readonly proofFile?: File | Blob | null;
  readonly proofFileName?: string;
}

export interface ReviewPaymentInput {
  readonly decision: "confirmed" | "rejected";
  readonly rejectionReason?: string;
  readonly idempotencyKey: string;
}

export interface CreateVenueInput {
  readonly name: string;
  readonly capacity?: number;
  readonly timezone?: string;
  readonly amoEnumValue?: string;
  readonly id?: string;
}

export interface UpdateVenueInput {
  readonly name?: string;
  readonly capacity?: number;
  readonly timezone?: string;
  readonly isActive?: boolean;
}

export interface ReassignInstructorInput {
  readonly primaryInstructorId: string;
  readonly idempotencyKey: string;
}

export interface MapVenuesInput {
  readonly enums: ReadonlyArray<{ readonly enumId: number | string; readonly name: string }>;
}

function readCookie(name: string): string | null {
  if (typeof document === "undefined") {
    return null;
  }
  const parts = document.cookie.split(";").map((p) => p.trim());
  for (const part of parts) {
    if (part.startsWith(`${name}=`)) {
      return decodeURIComponent(part.slice(name.length + 1));
    }
  }
  return null;
}

function parseJson(text: string): unknown {
  if (!text) {
    return null;
  }
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

export class ApiClient {
  private readonly baseUrl: string;
  private readonly getCsrfToken: () => string | null | undefined;
  private readonly fetchImpl: typeof fetch;
  private csrfOverride: string | null = null;
  private sessionTokenOverride: string | null = null;

  constructor(options: ApiClientOptions = {}) {
    this.baseUrl = (options.baseUrl ?? "").replace(/\/$/, "");
    this.getCsrfToken =
      options.getCsrfToken ??
      (() => this.csrfOverride ?? readCookie("csrf_token") ?? readCookie("csrf"));
    this.fetchImpl = options.fetchImpl ?? fetch.bind(globalThis);
  }

  setCsrfToken(token: string | null): void {
    this.csrfOverride = token;
  }

  async getSession(): Promise<Session> {
    const session = await this.request("GET", "/api/v1/auth/session", SessionSchema);
    if (session.csrfToken) {
      this.setCsrfToken(session.csrfToken);
    }
    return session;
  }

  async exchangeSessionHandoff(token: string): Promise<Session> {
    const session = await this.request(
      "POST",
      "/api/v1/auth/session/handoff",
      HandoffSessionSchema,
      { token },
    );
    this.sessionTokenOverride = session.sessionToken;
    if (session.csrfToken) this.setCsrfToken(session.csrfToken);
    return session;
  }

  getCalendar(query: CalendarRangeQuery): Promise<CalendarQueryResult> {
    const params = new URLSearchParams({ from: query.from, to: query.to });
    if (query.instructorId) params.set("instructorId", query.instructorId);
    return this.request("GET", `/api/v1/calendar?${params}`, CalendarQueryResultSchema);
  }

  getAmoAccount(): Promise<AmoAccount> {
    return this.request("GET", "/api/v1/integrations/amo/account", AmoAccountSchema);
  }

  getDealContext(leadId: number): Promise<DealContext> {
    return this.request(
      "GET",
      `/api/v1/deals/${encodeURIComponent(String(leadId))}/context`,
      DealContextSchema,
    );
  }

  createDealEnrollment(
    leadId: number,
    idempotencyKey: string,
    /** Required when the deal carries several linked courses (§3.2). */
    amoCourseElementId?: number,
  ): Promise<CreateDealEnrollmentResult> {
    return this.request(
      "POST",
      `/api/v1/deals/${encodeURIComponent(String(leadId))}/enrollment`,
      CreateDealEnrollmentResultSchema,
      {
        idempotencyKey,
        ...(amoCourseElementId !== undefined ? { amoCourseElementId } : {}),
      },
    );
  }

  /** Rewrites the deal's money/training panel in amo now, instead of waiting for the sweep. */
  refreshDealShowcase(leadId: number): Promise<{ leadId: number; written: boolean }> {
    return this.request(
      "POST",
      `/api/v1/deals/${encodeURIComponent(String(leadId))}/showcase`,
      z.object({ leadId: z.number().int(), written: z.boolean() }),
      {},
    );
  }

  getDealLedger(leadId: number): Promise<DealLedger> {
    return this.request(
      "GET",
      `/api/v1/ledger/deals/${encodeURIComponent(String(leadId))}`,
      DealLedgerSchema,
    );
  }

  /** «Оформить»: freezes the card prices, links the products, writes the amo budget (§2). */
  createLedgerContract(body: {
    leadId: number;
    lines: ReadonlyArray<{ courseElementId: number; quantity?: number }>;
    usdtRate?: number;
    reason?: string;
    idempotencyKey: string;
  }): Promise<LedgerContract> {
    return this.request("POST", "/api/v1/ledger/contracts", LedgerContractSchema, body);
  }

  submitLedgerPayment(body: {
    leadId: number;
    /** Minor units: cents for USDT, kopecks for RUB, whole IDR/THB. */
    amountOriginalMinor: number;
    currencyOriginal: OriginalCurrency;
    method: LedgerMethod;
    rateToLocal?: number;
    usdtRate?: number;
    surchargePercent?: number;
    operationType?: "deposit" | "refund";
    receiptUrl?: string;
    comment?: string;
    autoConfirm?: boolean;
    /** §7.4: переплата — явное решение менеджера. */
    allowOverpay?: boolean;
    idempotencyKey: string;
  }): Promise<LedgerPayment> {
    return this.request("POST", "/api/v1/ledger/payments", LedgerPaymentSchema, body);
  }

  /** Ручной курс: число человека рынок не перезаписывает (§5). */
  recordFxRate(body: {
    quote: "IDR" | "THB" | "RUB";
    rate: number;
    base?: "USDT" | "RUB";
  }): Promise<FxQuote> {
    return this.request("POST", "/api/v1/admin/fx", FxQuoteSchema, body);
  }

  listFxHistory(
    quote: "IDR" | "THB" | "RUB",
    limit = 20,
    base: "USDT" | "RUB" = "USDT",
  ): Promise<FxHistoryEntry[]> {
    const params = new URLSearchParams({ base, quote, limit: String(limit) });
    return this.request(
      "GET",
      `/api/v1/admin/fx-history?${params.toString()}`,
      FxHistoryResponseSchema,
    ).then((response) => response.items);
  }

  /** Очередь решений менеджера: поступления журнала, по умолчанию ожидающие. */
  /** Ученики инструктора: все сделки, которые он ведёт. */
  async listInstructorStudents(): Promise<InstructorStudent[]> {
    const response = await this.request(
      "GET",
      "/api/v1/instructor/students",
      InstructorStudentsResponseSchema,
    );
    return response.items;
  }

  /** Сколько принёс каждый сотрудник за период; инструктору сервер отдаёт только его строку. */
  async getTakings(period: { from?: string; to?: string } = {}): Promise<Takings> {
    const params = new URLSearchParams();
    if (period.from) params.set("from", period.from);
    if (period.to) params.set("to", period.to);
    const qs = params.toString();
    return this.request(
      "GET",
      `/api/v1/ledger/takings${qs ? `?${qs}` : ""}`,
      TakingsResponseSchema,
    );
  }

  async listLedgerPayments(
    status?: "pending" | "confirmed" | "rejected",
  ): Promise<LedgerPayment[]> {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    const qs = params.toString();
    const response = await this.request(
      "GET",
      `/api/v1/ledger/payments${qs ? `?${qs}` : ""}`,
      LedgerPaymentsResponseSchema,
    );
    return response.items;
  }

  reviewLedgerPayment(
    paymentId: string,
    body: { decision: "confirmed" | "rejected"; reason?: string; idempotencyKey: string },
  ): Promise<LedgerPayment> {
    return this.request(
      "POST",
      `/api/v1/ledger/payments/${encodeURIComponent(paymentId)}/review`,
      LedgerPaymentSchema,
      body,
    );
  }

  getOccupancy(query: CalendarRangeQuery) {
    const params = new URLSearchParams({ from: query.from, to: query.to });
    return this.request("GET", `/api/v1/calendar/occupancy?${params}`, OccupancyResponseSchema);
  }

  /**
   * Что случится, если передать сделку этому инструктору. Читающая половина: менеджер видит
   * список уроков и наложения до подтверждения, а не узнаёт о них из отказа.
   */
  previewDealInstructorTransfer(
    leadId: number,
    toInstructorId: string,
    keepConflictingTimes = false,
  ): Promise<DealTransferPlan> {
    const params = new URLSearchParams({
      toInstructorId,
      ...(keepConflictingTimes ? { keepConflictingTimes: "true" } : {}),
    });
    return this.request(
      "GET",
      `/api/v1/deals/${leadId}/instructor-transfer/preview?${params.toString()}`,
      DealTransferPlanSchema,
    );
  }

  transferDealInstructor(
    leadId: number,
    body: {
      toInstructorId: string;
      keepConflictingTimes: boolean;
      reason: string;
      idempotencyKey: string;
    },
  ): Promise<DealTransferPlan> {
    return this.request(
      "POST",
      `/api/v1/deals/${leadId}/instructor-transfer`,
      DealTransferPlanSchema,
      body,
    );
  }

  async listLocations(): Promise<Location[]> {
    const res = await this.request("GET", "/api/v1/locations", LocationsResponseSchema);
    return res.items;
  }

  async listVenues(): Promise<Venue[]> {
    const res = await this.request("GET", "/api/v1/venues", VenuesResponseSchema);
    return res.items;
  }

  async listInstructors(): Promise<Instructor[]> {
    const res = await this.request("GET", "/api/v1/instructors", InstructorsResponseSchema);
    return res.items;
  }

  getLesson(id: string): Promise<ScheduledLessonRecord> {
    return this.request(
      "GET",
      `/api/v1/lessons/${encodeURIComponent(id)}`,
      ScheduledLessonRecordSchema,
    );
  }

  scheduleLesson(id: string, body: ScheduleLessonInput): Promise<ScheduleMutationResult> {
    return this.request(
      "POST",
      `/api/v1/lessons/${encodeURIComponent(id)}/schedule`,
      ScheduleMutationResultSchema,
      body,
    );
  }

  rescheduleLesson(id: string, body: RescheduleLessonInput): Promise<ScheduleMutationResult> {
    return this.request(
      "POST",
      `/api/v1/lessons/${encodeURIComponent(id)}/reschedule`,
      ScheduleMutationResultSchema,
      body,
    );
  }

  cancelLesson(id: string, body: VersionedReasonInput): Promise<ScheduleMutationResult> {
    return this.request(
      "POST",
      `/api/v1/lessons/${encodeURIComponent(id)}/cancel`,
      ScheduleMutationResultSchema,
      body,
    );
  }

  markNoShow(id: string, body: VersionedReasonInput): Promise<ScheduleMutationResult> {
    return this.request(
      "POST",
      `/api/v1/lessons/${encodeURIComponent(id)}/no-show`,
      ScheduleMutationResultSchema,
      body,
    );
  }

  /** Предписание об оплате за курс: способ и приписка, которые увидит инструктор. */
  setEnrollmentSettlement(
    enrollmentId: string,
    body: { method: SettlementMethod | null; note?: string | null },
  ): Promise<Enrollment> {
    return this.request(
      "PATCH",
      `/api/v1/enrollments/${encodeURIComponent(enrollmentId)}/settlement`,
      EnrollmentSchema,
      body,
    );
  }

  async listEnrollmentsByLead(leadId: number): Promise<Enrollment[]> {
    const res = await this.request(
      "GET",
      `/api/v1/enrollments/by-lead/${encodeURIComponent(String(leadId))}`,
      EnrollmentsByLeadSchema,
    );
    return res.items;
  }

  async listEnrollmentLessons(enrollmentId: string): Promise<ScheduledLessonRecord[]> {
    const res = await this.request(
      "GET",
      `/api/v1/enrollments/${encodeURIComponent(enrollmentId)}/lessons`,
      LessonsListSchema,
    );
    return res.items;
  }

  async listUnscheduledLessons(): Promise<UnscheduledLesson[]> {
    const res = await this.request(
      "GET",
      "/api/v1/lessons/unscheduled",
      UnscheduledLessonsResponseSchema,
    );
    return res.items;
  }

  /** Какие пути входа предлагает этот стенд: пароль, Telegram, подсказка про amo. */
  getAuthMethods(): Promise<AuthMethods> {
    return this.request("GET", "/api/v1/auth/methods", AuthMethodsSchema);
  }

  /** Вход по личной учётке: роль приходит из записи, запрос её не выбирает. */
  async loginWithPassword(login: string, password: string): Promise<PasswordLoginResult> {
    const result = await this.request("POST", "/api/v1/auth/login", PasswordLoginResultSchema, {
      login,
      password,
    });
    if (result.csrfToken) this.setCsrfToken(result.csrfToken);
    return result;
  }

  async listAppUsers(): Promise<AppUser[]> {
    const res = await this.request("GET", "/api/v1/admin/users", AppUsersResponseSchema);
    return res.items;
  }

  createAppUser(body: {
    login: string;
    displayName: string;
    role: "manager" | "admin";
    password: string;
    amoUserId?: number;
  }): Promise<AppUser> {
    return this.request("POST", "/api/v1/admin/users", AppUserSchema, body);
  }

  updateAppUser(
    id: string,
    body: {
      displayName?: string;
      role?: "manager" | "admin";
      password?: string;
      amoUserId?: number | null;
      isActive?: boolean;
    },
  ): Promise<AppUser> {
    return this.request(
      "PATCH",
      `/api/v1/admin/users/${encodeURIComponent(id)}`,
      AppUserSchema,
      body,
    );
  }

  /**
   * Выход: сессия отзывается на сервере, куки чистятся там же. CSRF-токен сбрасываем сами —
   * иначе следующий вход подпишет запрос токеном от уже мёртвой сессии.
   */
  async logout(options: { allDevices?: boolean } = {}): Promise<LogoutResult> {
    const result = await this.request(
      "POST",
      "/api/v1/auth/logout",
      LogoutResultSchema,
      options.allDevices ? { allDevices: true } : {},
    );
    this.setCsrfToken(null);
    return result;
  }

  async loginTelegramMiniApp(initData: string): Promise<AuthBootstrap> {
    const result = await this.request(
      "POST",
      "/api/v1/auth/telegram/miniapp",
      AuthBootstrapSchema,
      { initData },
    );
    if (result.csrfToken) {
      this.setCsrfToken(result.csrfToken);
    }
    return result;
  }

  async startTelegramOidc(): Promise<{ authorizationUrl: string; state?: string }> {
    const started = await this.request("GET", "/api/v1/auth/telegram/oidc/start", OidcStartSchema);
    return started.state
      ? { authorizationUrl: started.authorizationUrl, state: started.state }
      : { authorizationUrl: started.authorizationUrl };
  }

  async completeTelegramOidc(code: string, state: string): Promise<AuthBootstrap> {
    const params = new URLSearchParams({ code, state });
    const result = await this.request(
      "GET",
      `/api/v1/auth/telegram/oidc/callback?${params}`,
      AuthBootstrapSchema,
    );
    if (result.csrfToken) {
      this.setCsrfToken(result.csrfToken);
    }
    return result;
  }

  /** Передать запись следующего урока менеджеру: задача в amo ставится на его стороне. */
  handoffNextLesson(
    enrollmentId: string,
    body: { comment?: string; idempotencyKey: string },
  ): Promise<HandoffNextResult> {
    return this.request(
      "POST",
      `/api/v1/instructor/enrollments/${encodeURIComponent(enrollmentId)}/handoff-next`,
      HandoffNextResultSchema,
      body,
    );
  }

  getInstructorSchedule(query: CalendarRangeQuery): Promise<InstructorScheduleView> {
    const params = new URLSearchParams({ from: query.from, to: query.to });
    return this.request(
      "GET",
      `/api/v1/instructor/schedule?${params}`,
      InstructorScheduleViewSchema,
    );
  }

  getInstructorLesson(id: string): Promise<OwnLessonDetail> {
    return this.request(
      "GET",
      `/api/v1/instructor/lessons/${encodeURIComponent(id)}`,
      OwnLessonDetailSchema,
    );
  }

  async getNextUnscheduledLesson(enrollmentId: string): Promise<NextUnscheduledLesson | null> {
    const res = await this.request(
      "GET",
      `/api/v1/instructor/enrollments/${encodeURIComponent(enrollmentId)}/next-unscheduled`,
      NextUnscheduledResponseSchema,
    );
    return res.item;
  }

  markClientArrived(id: string, body: VersionedActionInput): Promise<ScheduleMutationResult> {
    return this.request(
      "POST",
      `/api/v1/lessons/${encodeURIComponent(id)}/arrive`,
      ScheduleMutationResultSchema,
      {
        expectedVersion: body.expectedVersion,
        idempotencyKey: body.idempotencyKey,
      },
    );
  }

  undoArrival(id: string, body: VersionedActionInput): Promise<ScheduleMutationResult> {
    return this.request(
      "POST",
      `/api/v1/lessons/${encodeURIComponent(id)}/back`,
      ScheduleMutationResultSchema,
      {
        expectedVersion: body.expectedVersion,
        idempotencyKey: body.idempotencyKey,
        ...(body.comment !== undefined ? { comment: body.comment } : {}),
      },
    );
  }

  completeLesson(id: string, body: VersionedActionInput): Promise<ScheduleMutationResult> {
    return this.request(
      "POST",
      `/api/v1/lessons/${encodeURIComponent(id)}/complete`,
      ScheduleMutationResultSchema,
      {
        expectedVersion: body.expectedVersion,
        idempotencyKey: body.idempotencyKey,
        ...(body.comment !== undefined ? { comment: body.comment } : {}),
      },
    );
  }

  addLessonNote(
    id: string,
    body: { text: string; idempotencyKey: string },
  ): Promise<Record<string, unknown>> {
    return this.request(
      "POST",
      `/api/v1/lessons/${encodeURIComponent(id)}/notes`,
      InstructorNoteResultSchema,
      body,
    );
  }

  /**
   * Чек поступления: файл уходит в amo Drive, обратно приходит ссылка для поля «Чек оплаты».
   * Старый `POST /api/v1/payments` закрыт — деньги идут в журнал (§7.4).
   */
  async uploadLedgerReceipt(input: {
    leadId: number;
    file: Blob;
    fileName?: string;
  }): Promise<{ fileId: string; url: string | null }> {
    const form = new FormData();
    form.set("leadId", String(input.leadId));
    form.set(
      "file",
      input.file,
      input.fileName ?? (input.file instanceof File ? input.file.name : "receipt.bin"),
    );

    const headers: Record<string, string> = { Accept: "application/json" };
    const csrf = this.getCsrfToken();
    if (csrf) headers["x-csrf-token"] = csrf;

    const response = await this.fetchImpl(`${this.baseUrl}/api/v1/ledger/receipts`, {
      method: "POST",
      credentials: "include",
      headers,
      body: form,
    });
    const text = await response.text();
    const payload = parseJson(text);
    if (!response.ok) {
      throw parseApiError(response.status, payload);
    }
    const parsed = LedgerReceiptSchema.safeParse(payload);
    if (!parsed.success) {
      throw new ApiClientError("Response failed contract validation", {
        statusCode: response.status,
        code: "contract_validation_error",
        details: { issues: parsed.error.issues },
      });
    }
    return parsed.data;
  }

  async listPayments(status?: PaymentReport["status"]): Promise<PaymentReport[]> {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    const qs = params.toString();
    const res = await this.request(
      "GET",
      `/api/v1/payments${qs ? `?${qs}` : ""}`,
      PaymentReportsResponseSchema,
    );
    return res.items;
  }

  getPayment(id: string): Promise<PaymentReport> {
    return this.request("GET", `/api/v1/payments/${encodeURIComponent(id)}`, PaymentReportSchema);
  }

  reviewPayment(id: string, body: ReviewPaymentInput): Promise<PaymentReport> {
    return this.request(
      "POST",
      `/api/v1/payments/${encodeURIComponent(id)}/review`,
      PaymentReportSchema,
      {
        decision: body.decision,
        idempotencyKey: body.idempotencyKey,
        ...(body.rejectionReason !== undefined ? { rejectionReason: body.rejectionReason } : {}),
      },
    );
  }

  createVenue(body: CreateVenueInput): Promise<Venue> {
    return this.request("POST", "/api/v1/venues", VenueSchema, body);
  }

  updateVenue(id: string, body: UpdateVenueInput): Promise<Venue> {
    return this.request("PATCH", `/api/v1/venues/${encodeURIComponent(id)}`, VenueSchema, body);
  }

  async listEnrollments(status?: "active" | "completed" | "terminated"): Promise<Enrollment[]> {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    const qs = params.toString();
    const res = await this.request(
      "GET",
      `/api/v1/enrollments${qs ? `?${qs}` : ""}`,
      EnrollmentsListSchema,
    );
    return res.items;
  }

  reassignEnrollmentInstructor(
    enrollmentId: string,
    body: ReassignInstructorInput,
  ): Promise<Enrollment> {
    return this.request(
      "POST",
      `/api/v1/enrollments/${encodeURIComponent(enrollmentId)}/reassign-instructor`,
      EnrollmentSchema,
      body,
    );
  }

  async listOperationalLogs(limit = 100): Promise<OperationalLogItem[]> {
    const params = new URLSearchParams({ limit: String(limit) });
    const res = await this.request(
      "GET",
      `/api/v1/admin/logs?${params}`,
      OperationalLogsResponseSchema,
    );
    return res.items;
  }

  /**
   * amoCRM is the source of truth for these catalogs; `fieldOwnership` in the response says
   * which fields are editable here (`two_way`/`local`) versus amo-only (`amo`).
   */
  listAdminCatalogs(): Promise<AdminCatalogs> {
    return this.request("GET", "/api/v1/admin/catalogs", AdminCatalogsResponseSchema);
  }

  updateCatalogElement(
    catalog: EditableAdminCatalog,
    elementId: number,
    fields: Record<string, CatalogFieldValue>,
  ): Promise<UpdateCatalogElementResponse> {
    return this.request(
      "PATCH",
      `/api/v1/admin/catalogs/${catalog}/elements/${encodeURIComponent(String(elementId))}`,
      UpdateCatalogElementResponseSchema,
      { fields },
    );
  }

  syncCatalogs(): Promise<SyncCatalogsResponse> {
    return this.request("POST", "/api/v1/admin/catalogs/sync", SyncCatalogsResponseSchema);
  }

  async listInstructorMappings(): Promise<InstructorMapping[]> {
    const res = await this.request(
      "GET",
      "/api/v1/admin/mappings/instructors",
      InstructorMappingsResponseSchema,
    );
    return res.items;
  }

  mapVenueEnums(body: MapVenuesInput): Promise<Record<string, unknown>> {
    return this.request("POST", "/api/v1/admin/mappings/venues", VenueMappingResultSchema, body);
  }

  getFinanceSettings(): Promise<FinanceSettings> {
    return this.request("GET", "/api/v1/admin/finance-settings", FinanceSettingsSchema);
  }

  saveFinanceSettings(body: {
    currencySurcharges: Record<string, number>;
  }): Promise<FinanceSettings> {
    return this.request("PUT", "/api/v1/admin/finance-settings", FinanceSettingsSchema, body);
  }

  /** The rate a payment form prefills with; `stale` means the source was unreachable (§5). */
  getFxQuote(quote: string, base = "USDT"): Promise<FxQuote> {
    const params = new URLSearchParams({ base, quote });
    return this.request("GET", `/api/v1/ledger/fx?${params.toString()}`, FxQuoteSchema);
  }

  getBackupMetadata(): Promise<BackupMetadata> {
    return this.request("GET", "/api/v1/admin/backup", BackupMetadataSchema);
  }

  getAmoStatus(): Promise<AmoStatus> {
    return this.request("GET", "/api/v1/admin/amo/status", AmoStatusSchema);
  }

  startAmoOAuth(): Promise<AmoOAuthStart> {
    return this.request("GET", "/api/v1/auth/amo/oauth/start", AmoOAuthStartSchema);
  }

  async downloadBackup(): Promise<BackupDownloadResult> {
    const headers: Record<string, string> = { Accept: "application/zip" };
    const csrf = this.getCsrfToken();
    if (csrf) headers["x-csrf-token"] = csrf;

    const response = await this.fetchImpl(`${this.baseUrl}/api/v1/admin/backup`, {
      method: "POST",
      credentials: "include",
      headers,
    });

    if (!response.ok) {
      const text = await response.text();
      throw parseApiError(response.status, parseJson(text));
    }

    const blob = await response.blob();
    const disposition = response.headers.get("content-disposition") ?? "";
    const match = disposition.match(/filename="([^"]+)"/);
    const filename = match?.[1] ?? "pwz-backup.zip";
    const checksumSha256 = response.headers.get("x-backup-checksum-sha256") ?? "";
    const archiveChecksumSha256 = response.headers.get("x-backup-archive-checksum-sha256") ?? "";
    const auditId = response.headers.get("x-backup-audit-id") ?? "";
    const pendingRaw = response.headers.get("x-backup-pending-uploads");
    const pendingUploads = pendingRaw ? Number(pendingRaw) : undefined;

    return {
      blob,
      filename,
      checksumSha256,
      archiveChecksumSha256,
      auditId,
      ...(pendingUploads !== undefined && !Number.isNaN(pendingUploads) ? { pendingUploads } : {}),
    };
  }

  private async request<T>(
    method: string,
    path: string,
    schema: z.ZodType<T>,
    body?: unknown,
  ): Promise<T> {
    const headers: Record<string, string> = {
      Accept: "application/json",
    };
    if (body !== undefined) {
      headers["Content-Type"] = "application/json";
    }
    if (method !== "GET" && method !== "HEAD") {
      const csrf = this.getCsrfToken();
      if (csrf) {
        headers["x-csrf-token"] = csrf;
      }
    }
    if (this.sessionTokenOverride) {
      headers["x-session-token"] = this.sessionTokenOverride;
    }

    const init: RequestInit = {
      method,
      credentials: "include",
      headers,
    };
    if (body !== undefined) {
      init.body = JSON.stringify(body);
    }

    let response: Response;
    try {
      response = await this.fetchImpl(`${this.baseUrl}${path}`, init);
    } catch (cause) {
      const message =
        cause instanceof Error && cause.message ? cause.message : "Нет связи с сервером";
      throw new ApiClientError(message, {
        statusCode: 0,
        code: "network_error",
      });
    }

    const text = await response.text();
    const payload = parseJson(text);

    if (!response.ok) {
      throw parseApiError(response.status, payload);
    }

    const parsed = schema.safeParse(payload);
    if (!parsed.success) {
      throw new ApiClientError("Ответ сервера не соответствует контракту", {
        statusCode: response.status,
        code: "contract_validation_error",
        details: { issues: parsed.error.issues },
      });
    }
    return parsed.data;
  }
}

export function createApiClient(options?: ApiClientOptions): ApiClient {
  return new ApiClient(options);
}

export type { ApiClient as ApiClientType };
